/**
 * MobPanel — Live encounter mob management
 *
 * Reads directly from mobStore (DB-backed). Provides inline HP editing,
 * condition management, damage/heal buttons, and kill/remove actions.
 */

import { useState, useCallback, useRef } from 'react';
import { useMobStore, type Mob, getAbilityMod } from '../../stores/mobStore';
import { useTokenStore } from '../../stores/tokenStore';
import { useCampaignStore } from '../../stores/campaignStore';
import { useDiceStore } from '../../stores/diceStore';
import { useChatStore } from '../../stores/chatStore';
import { useIsoSpriteStore } from '../../stores/isoSpriteStore';
import { Icon, type IconName } from '../Icon';
import { getHpTier } from '@machina/core';
import type { HpTier, ConditionType } from '@machina/core';
import { trpc } from '../../lib/trpc';
import { MobStatSheet } from './MobStatSheet';
import { buildMobSpriteParams } from '../../lib/mobSpritePrompt';
import { rollSystemCheck } from '../../lib/system-roll';

// =============================================================================
// CONSTANTS
// =============================================================================

const GOLD = '#d4af37';

const HP_TIER_CONFIG: Record<HpTier, { color: string; label: string }> = {
  healthy: { color: '#4ade80', label: 'Healthy' },
  wounded: { color: '#a3e635', label: 'Wounded' },
  bloody: { color: '#f59e0b', label: 'Bloodied' },
  critical: { color: '#ef4444', label: 'Critical' },
  unconscious: { color: '#6b7280', label: 'Unconscious' },
  dead: { color: '#374151', label: 'Dead' },
};

const COMMON_CONDITIONS: ConditionType[] = [
  'blinded', 'charmed', 'frightened', 'grappled', 'incapacitated',
  'paralyzed', 'poisoned', 'prone', 'restrained', 'stunned',
];

const CONDITION_ICONS: Partial<Record<ConditionType, IconName>> = {
  blinded: 'eye-off',
  charmed: 'heart-pulse',
  frightened: 'alert',
  paralyzed: 'lock',
  poisoned: 'droplet',
  stunned: 'lightning',
};

type AbilityName = 'strength' | 'dexterity' | 'constitution' | 'intelligence' | 'wisdom' | 'charisma';
const ABILITY_ABBREV: Record<AbilityName, string> = {
  strength: 'STR', dexterity: 'DEX', constitution: 'CON',
  intelligence: 'INT', wisdom: 'WIS', charisma: 'CHA',
};

// =============================================================================
// MOB ROW (compact)
// =============================================================================

function MobRow({
  mob,
  isExpanded,
  onToggle,
}: {
  mob: Mob;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const hpTier = getHpTier(mob.currentHp, mob.maxHp);
  const tierConfig = HP_TIER_CONFIG[mob.dead ? 'dead' : hpTier];
  const hpPercent = mob.maxHp > 0 ? Math.round((mob.currentHp / mob.maxHp) * 100) : 0;

  return (
    <div
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 8px',
        cursor: 'pointer',
        backgroundColor: isExpanded ? 'rgba(212, 175, 55, 0.08)' : 'transparent',
        borderLeft: isExpanded ? `2px solid ${GOLD}` : '2px solid transparent',
        opacity: mob.dead ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        if (!isExpanded) e.currentTarget.style.backgroundColor = 'rgba(192, 192, 192, 0.04)';
      }}
      onMouseLeave={(e) => {
        if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {/* HP dot */}
      <div style={{
        width: 7, height: 7,
        borderRadius: '50%',
        backgroundColor: tierConfig.color,
        boxShadow: `0 0 4px ${tierConfig.color}`,
        flexShrink: 0,
      }} />

      {/* Name */}
      <div style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 500, color: '#e0e0e0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {mob.displayName || mob.name}
        {mob.dead && <span style={{ marginLeft: 4, fontSize: 9, color: '#6b7280' }}>DEAD</span>}
      </div>

      {/* Condition dots */}
      {mob.conditions.length > 0 && (
        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
          {mob.conditions.slice(0, 3).map((c) => {
            const icon = CONDITION_ICONS[c.type as ConditionType];
            return icon ? (
              <Icon key={c.type} name={icon} size={10} color="#f59e0b" />
            ) : (
              <span key={c.type} style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#f59e0b', display: 'inline-block' }} />
            );
          })}
          {mob.conditions.length > 3 && (
            <span style={{ fontSize: 9, color: 'rgba(192, 192, 192, 0.5)' }}>+{mob.conditions.length - 3}</span>
          )}
        </div>
      )}

      {/* HP fraction */}
      <span style={{ fontSize: 10, fontFamily: 'monospace', color: tierConfig.color, flexShrink: 0 }}>
        {mob.currentHp}/{mob.maxHp}
      </span>

      {/* HP mini bar */}
      <div style={{ width: 30, height: 3, backgroundColor: 'rgba(192, 192, 192, 0.1)', borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
        <div style={{ width: `${hpPercent}%`, height: '100%', backgroundColor: tierConfig.color }} />
      </div>
    </div>
  );
}

// =============================================================================
// MOB DETAIL (expanded inline)
// =============================================================================

function MobDetail({ mob: mobProp, onClose }: { mob: Mob; onClose: () => void }) {
  // Per-mob selector — re-renders specifically when THIS mob changes.
  // Previously we relied on the parent's `useMobStore(s => s.mobs)` Map-identity
  // check which was reliable in theory but intermittently skipped renders under
  // rapid mutations (the "works on second try" bug).
  const liveMob = useMobStore((s) => s.mobs.get(mobProp.id));
  const mob = liveMob ?? mobProp;

  const damageMob = useMobStore((s) => s.damageMob);
  const healMob = useMobStore((s) => s.healMob);
  const applyCondition = useMobStore((s) => s.applyCondition);
  const removeCondition = useMobStore((s) => s.removeCondition);
  const isReady = useDiceStore((s) => s.isReady);
  const addDiceRoll = useChatStore((s) => s.addDiceRoll);

  const [customAmount, setCustomAmount] = useState('');
  const [isRolling, setIsRolling] = useState(false);
  const [editingHp, setEditingHp] = useState(false);
  const [hpInput, setHpInput] = useState('');
  const [savingToBestiary, setSavingToBestiary] = useState(false);
  const [savedBestiary, setSavedBestiary] = useState(false);
  const [showFullSheet, setShowFullSheet] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [introTagline, setIntroTagline] = useState('');
  const [introDescription, setIntroDescription] = useState('');
  const [introHint, setIntroHint] = useState('');

  /** Set HP to an absolute value via damage/heal delta (so attention + death state apply). */
  const setHpAbsolute = (target: number) => {
    const clamped = Math.max(0, Math.min(mob.maxHp, Math.floor(target)));
    const delta = clamped - mob.currentHp;
    if (delta < 0) damageMob(mob.id, -delta);
    else if (delta > 0) healMob(mob.id, delta);
    setEditingHp(false);
  };

  const commitHpEdit = () => {
    const n = parseInt(hpInput);
    if (!isNaN(n)) setHpAbsolute(n);
    else setEditingHp(false);
  };

  const hpTier = getHpTier(mob.currentHp, mob.maxHp);
  const tierConfig = HP_TIER_CONFIG[mob.dead ? 'dead' : hpTier];

  const handleDamage = (amount: number) => {
    damageMob(mob.id, amount);
    setCustomAmount('');
  };
  const handleHeal = (amount: number) => {
    healMob(mob.id, amount);
    setCustomAmount('');
  };
  const handleKill = async () => {
    try { await trpc.bestiary.killMob.mutate({ id: mob.id }); } catch { /* realtime will catch up */ }
    damageMob(mob.id, mob.currentHp); // Local immediate
  };
  const handleRevive = async () => {
    try { await trpc.bestiary.reviveMob.mutate({ id: mob.id }); } catch { /* fallback */ }
    healMob(mob.id, mob.maxHp);
  };
  const handleRemove = async () => {
    try { await trpc.bestiary.removeMob.mutate({ id: mob.id }); } catch { /* fallback */ }
    useMobStore.getState().removeMob(mob.id);
    // Remove linked token
    const tokenStore = useTokenStore.getState();
    const linkedToken = tokenStore.getTokenByCombatantId(mob.id);
    if (linkedToken) tokenStore.removeToken(linkedToken.id);
    onClose();
  };

  /** Promote this live mob into a reusable bestiary template. */
  const handleSaveToBestiary = async () => {
    const campaignId = useCampaignStore.getState().currentCampaignId;
    if (!campaignId || savingToBestiary) return;
    setSavingToBestiary(true);
    try {
      await trpc.bestiary.create.mutate({
        campaignId,
        name: mob.name,
        cr: mob.cr ? String(mob.cr) : undefined,
        size: mob.size || 'medium',
        creatureType: mob.type || undefined,
        ac: mob.ac,
        maxHp: mob.maxHp,
        abilities: {
          str: mob.strength, dex: mob.dexterity, con: mob.constitution,
          int: mob.intelligence, wis: mob.wisdom, cha: mob.charisma,
        },
        actions: mob.actions.map((a) => ({
          name: a.name,
          attackBonus: a.attackBonus,
          damageDice: a.damageDice,
          damageType: a.damageType,
          range: a.range,
          description: a.description,
        })),
        speed: mob.speed,
        resistances: mob.damageResistances ? mob.damageResistances.split(',').map(s => s.trim()).filter(Boolean) : [],
        immunities: mob.damageImmunities ? mob.damageImmunities.split(',').map(s => s.trim()).filter(Boolean) : [],
        vulnerabilities: mob.damageVulnerabilities ? mob.damageVulnerabilities.split(',').map(s => s.trim()).filter(Boolean) : [],
        conditionImmunities: mob.conditionImmunities ? mob.conditionImmunities.split(',').map(s => s.trim()).filter(Boolean) : [],
        notes: mob.behaviorNotes,
        source: 'from-encounter',
      });
      setSavedBestiary(true);
    } catch (err) {
      console.error('[MobPanel] Save to Bestiary failed:', err);
    } finally {
      setSavingToBestiary(false);
    }
  };

  const activeConditions = new Set(mob.conditions.map((c) => c.type));

  return (
    <div style={{
      padding: '8px',
      backgroundColor: 'rgba(20, 15, 30, 0.5)',
      borderLeft: `2px solid ${GOLD}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    }}>
      {/* HP bar + label — click the HP total to edit directly */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10, marginBottom: 3 }}>
          <span style={{ color: tierConfig.color, fontWeight: 600 }}>{tierConfig.label}</span>
          {editingHp ? (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input
                type="number"
                autoFocus
                value={hpInput}
                onChange={(e) => setHpInput(e.target.value)}
                onBlur={commitHpEdit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitHpEdit();
                  if (e.key === 'Escape') setEditingHp(false);
                }}
                style={{
                  width: 44, padding: '1px 4px', fontSize: 10,
                  backgroundColor: 'rgba(20, 15, 30, 0.8)', color: '#e0e0e0',
                  border: `1px solid ${GOLD}`, borderRadius: 2, outline: 'none',
                  textAlign: 'right',
                }}
              />
              <span style={{ color: 'rgba(192, 192, 192, 0.6)', fontSize: 10 }}>/ {mob.maxHp}</span>
            </div>
          ) : (
            <span
              onClick={() => { setHpInput(String(mob.currentHp)); setEditingHp(true); }}
              title="Click to edit HP directly"
              style={{
                color: tierConfig.color, fontWeight: 600,
                cursor: 'pointer',
                padding: '1px 4px', borderRadius: 2,
                border: '1px dashed rgba(192, 192, 192, 0.25)',
              }}
            >
              {mob.currentHp} / {mob.maxHp}
            </span>
          )}
          <span style={{ color: 'rgba(192, 192, 192, 0.6)' }}>AC {mob.ac}</span>
        </div>
        <div style={{ height: 6, backgroundColor: 'rgba(192, 192, 192, 0.1)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{
            width: `${mob.maxHp > 0 ? (mob.currentHp / mob.maxHp) * 100 : 0}%`,
            height: '100%',
            backgroundColor: tierConfig.color,
            transition: 'width 0.2s',
          }} />
        </div>
        {mob.tempHp > 0 && (
          <div style={{ fontSize: 9, color: '#60a5fa', marginTop: 2 }}>+{mob.tempHp} temp HP</div>
        )}
      </div>

      {/* Damage / Heal buttons */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {[1, 5, 10].map((amt) => (
          <button
            key={`dmg-${amt}`}
            onClick={() => handleDamage(amt)}
            style={{
              padding: '2px 6px', fontSize: 10, fontWeight: 500,
              backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#ef4444',
              border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 3, cursor: 'pointer',
            }}
          >-{amt}</button>
        ))}
        <span style={{ width: 1, backgroundColor: 'rgba(192, 192, 192, 0.15)' }} />
        {[1, 5, 10].map((amt) => (
          <button
            key={`heal-${amt}`}
            onClick={() => handleHeal(amt)}
            style={{
              padding: '2px 6px', fontSize: 10, fontWeight: 500,
              backgroundColor: 'rgba(74, 222, 128, 0.15)', color: '#4ade80',
              border: '1px solid rgba(74, 222, 128, 0.3)', borderRadius: 3, cursor: 'pointer',
            }}
          >+{amt}</button>
        ))}
      </div>

      {/* Custom amount */}
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          type="number"
          value={customAmount}
          onChange={(e) => setCustomAmount(e.target.value)}
          placeholder="Custom"
          style={{
            flex: 1, padding: '2px 6px', fontSize: 10,
            backgroundColor: 'rgba(20, 15, 30, 0.6)', color: '#e0e0e0',
            border: '1px solid rgba(192, 192, 192, 0.15)', borderRadius: 3, outline: 'none',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const amt = parseInt(customAmount);
              if (!isNaN(amt) && amt > 0) handleDamage(amt);
            }
          }}
        />
        <button
          onClick={() => { const amt = parseInt(customAmount); if (!isNaN(amt) && amt > 0) handleDamage(amt); }}
          style={{ padding: '2px 6px', fontSize: 10, backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 3, cursor: 'pointer' }}
        >Dmg</button>
        <button
          onClick={() => { const amt = parseInt(customAmount); if (!isNaN(amt) && amt > 0) handleHeal(amt); }}
          style={{ padding: '2px 6px', fontSize: 10, backgroundColor: 'rgba(74, 222, 128, 0.15)', color: '#4ade80', border: '1px solid rgba(74, 222, 128, 0.3)', borderRadius: 3, cursor: 'pointer' }}
        >Heal</button>
      </div>

      {/* Conditions — dimmed + disabled if the mob is immune (e.g. zombies are
          immune to poisoned/charmed; applying those was silently no-op before). */}
      <div>
        <div style={{ fontSize: 9, color: 'rgba(192, 192, 192, 0.5)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Conditions</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {COMMON_CONDITIONS.map((cond) => {
            const isActive = activeConditions.has(cond);
            const immunities = mob.conditionImmunities.toLowerCase().split(',').map(s => s.trim()).filter(Boolean);
            const isImmune = immunities.some((imm) => imm === cond.toLowerCase());
            return (
              <button
                key={cond}
                disabled={isImmune}
                onClick={() => {
                  if (isImmune) return;
                  if (isActive) removeCondition(mob.id, cond);
                  else applyCondition(mob.id, cond);
                }}
                title={isImmune ? `${mob.displayName || mob.name} is immune to ${cond}` : undefined}
                style={{
                  padding: '1px 5px', fontSize: 9,
                  backgroundColor: isImmune
                    ? 'rgba(120, 120, 120, 0.05)'
                    : isActive ? 'rgba(245, 158, 11, 0.2)' : 'rgba(192, 192, 192, 0.05)',
                  color: isImmune
                    ? 'rgba(192, 192, 192, 0.25)'
                    : isActive ? '#f59e0b' : 'rgba(192, 192, 192, 0.4)',
                  border: `1px solid ${isImmune
                    ? 'rgba(120, 120, 120, 0.1)'
                    : isActive ? 'rgba(245, 158, 11, 0.3)' : 'rgba(192, 192, 192, 0.1)'}`,
                  borderRadius: 2,
                  cursor: isImmune ? 'not-allowed' : 'pointer',
                  textDecoration: isImmune ? 'line-through' : 'none',
                }}
              >{cond}</button>
            );
          })}
        </div>
      </div>

      {/* Saves — clickable to roll */}
      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {(['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'] as AbilityName[]).map((ability) => {
          const score = mob[ability];
          const mod = getAbilityMod(score);
          const profSave = mob.saves[ability];
          const saveBonus = profSave ?? mod;
          const isProficient = profSave !== undefined;
          return (
            <button
              key={ability}
              onClick={async () => {
                if (!isReady || isRolling) return;
                setIsRolling(true);
                try {
                  const check = await rollSystemCheck(
                    { type: 'saving-throw', ability, modifier: saveBonus },
                    { label: `${ABILITY_ABBREV[ability]} Save` },
                  );
                  if (check) {
                    addDiceRoll({
                      sender: 'GM',
                      characterName: mob.displayName || mob.name,
                      notation: check.notation,
                      result: check.result,
                      rollType: 'normal',
                      rollLabel: 'save',
                    });
                  }
                } finally {
                  setIsRolling(false);
                }
              }}
              disabled={!isReady || isRolling}
              title={`Roll ${ability} save (${isProficient ? 'proficient ' : ''}${saveBonus >= 0 ? '+' : ''}${saveBonus})`}
              style={{
                flex: '1 1 auto',
                minWidth: 44,
                padding: '3px 4px',
                fontSize: 9,
                fontFamily: 'monospace',
                backgroundColor: isProficient ? 'rgba(212, 175, 55, 0.15)' : 'rgba(0,0,0,0.2)',
                color: isProficient ? GOLD : 'rgba(192, 192, 192, 0.7)',
                border: `1px solid ${isProficient ? 'rgba(212, 175, 55, 0.4)' : 'rgba(192, 192, 192, 0.15)'}`,
                borderRadius: 3,
                cursor: isReady && !isRolling ? 'pointer' : 'not-allowed',
                opacity: isReady && !isRolling ? 1 : 0.5,
                textAlign: 'center',
                lineHeight: 1.3,
              }}
            >
              <div style={{ fontWeight: 600 }}>{ABILITY_ABBREV[ability]}</div>
              <div>{saveBonus >= 0 ? '+' : ''}{saveBonus}</div>
            </button>
          );
        })}
      </div>

      {/* Actions */}
      {mob.actions.length > 0 && (
        <div>
          <div style={{ fontSize: 9, color: 'rgba(192, 192, 192, 0.5)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</div>
          {mob.actions.map((a, i) => (
            <div key={i} style={{ fontSize: 10, color: 'rgba(192, 192, 192, 0.7)', marginBottom: 2 }}>
              <span style={{ fontWeight: 500 }}>{a.name}</span>
              <span style={{ marginLeft: 4 }}>+{a.attackBonus} · {a.damageDice} {a.damageType}</span>
            </div>
          ))}
        </div>
      )}

      {/* Kill / Revive / Remove */}
      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
        {!mob.dead ? (
          <button
            onClick={handleKill}
            style={{
              flex: 1, padding: '3px 8px', fontSize: 10, fontWeight: 600,
              backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#ef4444',
              border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 3, cursor: 'pointer',
            }}
          >Kill</button>
        ) : (
          <button
            onClick={handleRevive}
            style={{
              flex: 1, padding: '3px 8px', fontSize: 10, fontWeight: 600,
              backgroundColor: 'rgba(74, 222, 128, 0.15)', color: '#4ade80',
              border: '1px solid rgba(74, 222, 128, 0.3)', borderRadius: 3, cursor: 'pointer',
            }}
          >Revive</button>
        )}
        <button
          onClick={handleRemove}
          style={{
            padding: '3px 8px', fontSize: 10,
            backgroundColor: 'rgba(192, 192, 192, 0.05)', color: 'rgba(192, 192, 192, 0.5)',
            border: '1px solid rgba(192, 192, 192, 0.1)', borderRadius: 3, cursor: 'pointer',
          }}
        >Remove</button>
      </div>

      {/* Sprite & Images — GM can regen iso sprite, upload a custom one, or
          swap the flat token portrait used in 2D view. All persist to the
          bestiary template when the mob has one (so future spawns inherit). */}
      <MobSpriteControls mob={mob} />

      {/* Quick actions row */}
      <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
        <button
          onClick={() => setShowFullSheet(true)}
          title="Open full monster stat block"
          style={{
            padding: '3px 8px', fontSize: 10, fontWeight: 600,
            backgroundColor: 'rgba(212, 175, 55, 0.1)', color: GOLD,
            border: '1px solid rgba(212, 175, 55, 0.3)', borderRadius: 3, cursor: 'pointer',
            flex: 1,
          }}
        >
          Full Stat Block
        </button>
        <button
          onClick={() => setShowIntro(true)}
          title="Post a cinematic reveal card for this mob (image + tagline, no stats)"
          style={{
            padding: '3px 8px', fontSize: 10, fontWeight: 600,
            backgroundColor: 'rgba(136, 104, 198, 0.1)', color: '#b088d4',
            border: '1px solid rgba(136, 104, 198, 0.3)', borderRadius: 3, cursor: 'pointer',
            flex: 1,
          }}
        >
          Introduce
        </button>
        <button
          onClick={handleSaveToBestiary}
          disabled={savingToBestiary || savedBestiary}
          title="Save this mob's stats as a reusable bestiary template for future encounters"
          style={{
            padding: '3px 8px', fontSize: 10, fontWeight: 600,
            backgroundColor: savedBestiary ? 'rgba(74, 222, 128, 0.15)' : 'rgba(212, 175, 55, 0.1)',
            color: savedBestiary ? '#6fbd6f' : GOLD,
            border: `1px solid ${savedBestiary ? 'rgba(74, 222, 128, 0.3)' : 'rgba(212, 175, 55, 0.3)'}`,
            borderRadius: 3, cursor: savingToBestiary || savedBestiary ? 'default' : 'pointer',
            flex: 1,
          }}
        >
          {savingToBestiary ? 'Saving…' : savedBestiary ? '✓ Saved to Bestiary' : 'Save to Bestiary'}
        </button>
      </div>

      {showFullSheet && (
        <MobStatSheet mob={mob} onClose={() => setShowFullSheet(false)} />
      )}

      {/* Mob Intro composer — GM writes a cinematic reveal, posts to chat */}
      {showIntro && (
        <div
          onClick={() => setShowIntro(false)}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#0a080c', border: '1px solid rgba(212, 175, 55, 0.4)',
              borderRadius: 6, padding: 20, width: '100%', maxWidth: 460,
              fontFamily: 'Georgia, serif', color: '#f4e4bc',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: GOLD, marginBottom: 8 }}>
              Introduce {mob.displayName || mob.name}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(192,192,192,0.5)', marginBottom: 12 }}>
              Posts a reveal card with the token image. Stats stay hidden from players.
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: GOLD, letterSpacing: 0.5, marginBottom: 3 }}>TAGLINE</div>
              <input
                value={introTagline}
                onChange={(e) => setIntroTagline(e.target.value)}
                placeholder="Short dramatic line (e.g. 'The air smells of almonds and iron.')"
                maxLength={140}
                style={{
                  width: '100%', padding: '6px 8px', fontSize: 12,
                  background: 'rgba(20,15,30,0.8)', color: '#f4e4bc',
                  border: '1px solid rgba(212,175,55,0.3)', borderRadius: 3,
                  fontFamily: 'Georgia, serif', fontStyle: 'italic', outline: 'none',
                }}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: GOLD, letterSpacing: 0.5, marginBottom: 3 }}>DESCRIPTION</div>
              <textarea
                value={introDescription}
                onChange={(e) => setIntroDescription(e.target.value)}
                placeholder="1-3 sentences of visible traits / lore. NO mechanics."
                rows={3}
                style={{
                  width: '100%', padding: '6px 8px', fontSize: 12,
                  background: 'rgba(20,15,30,0.8)', color: '#f4e4bc',
                  border: '1px solid rgba(212,175,55,0.3)', borderRadius: 3,
                  fontFamily: 'Georgia, serif', resize: 'vertical', outline: 'none',
                }}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: GOLD, letterSpacing: 0.5, marginBottom: 3 }}>HINT (OPTIONAL)</div>
              <input
                value={introHint}
                onChange={(e) => setIntroHint(e.target.value)}
                placeholder="Subtle nudge, styled as a whisper"
                maxLength={120}
                style={{
                  width: '100%', padding: '6px 8px', fontSize: 12,
                  background: 'rgba(20,15,30,0.8)', color: '#f4e4bc',
                  border: '1px solid rgba(212,175,55,0.3)', borderRadius: 3,
                  fontFamily: 'Georgia, serif', fontStyle: 'italic', outline: 'none',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowIntro(false)}
                style={{
                  padding: '6px 14px', fontSize: 11, background: 'transparent',
                  color: 'rgba(192,192,192,0.6)', border: '1px solid rgba(192,192,192,0.2)',
                  borderRadius: 3, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Pull image from token (avatar/portrait fallback)
                  const linkedToken = useTokenStore.getState().getTokenByCombatantId(mob.id);
                  const imageUrl = linkedToken?.visuals?.imageUrl;
                  useChatStore.getState().addMobIntro({
                    name: mob.displayName || mob.name,
                    imageUrl,
                    tagline: introTagline.trim() || undefined,
                    description: introDescription.trim() || undefined,
                    hint: introHint.trim() || undefined,
                    size: mob.size,
                    creatureType: mob.type,
                  });
                  setIntroTagline('');
                  setIntroDescription('');
                  setIntroHint('');
                  setShowIntro(false);
                }}
                style={{
                  padding: '6px 14px', fontSize: 11, fontWeight: 700,
                  background: GOLD, color: '#1a0f0a',
                  border: 'none', borderRadius: 3, cursor: 'pointer',
                }}
              >
                Post to Chat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function MobPanel() {
  const mobs = useMobStore((s) => s.mobs);
  const campaignId = useCampaignStore((s) => s.currentCampaignId);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showDead, setShowDead] = useState(false);

  const allMobs = Array.from(mobs.values());
  const aliveMobs = allMobs.filter((m) => !m.dead);
  const deadMobs = allMobs.filter((m) => m.dead);

  const handleClearEncounter = useCallback(async () => {
    if (!campaignId) return;
    await useMobStore.getState().clearEncounter(campaignId);
  }, [campaignId]);

  /** Nuke every mob + orphan token on the board. PCs preserved.
   *  Wraps removeAllMobs (already handles orphan sweep) and logs the count. */
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const handleClearAllMobs = useCallback(async () => {
    const before = useMobStore.getState().mobs.size;
    await useMobStore.getState().removeAllMobs();
    console.log(`[MobPanel] Cleared ${before} mobs + any orphan tokens`);
    setConfirmClearAll(false);
  }, []);

  if (allMobs.length === 0) {
    return (
      <div style={{
        padding: 12,
        textAlign: 'center',
        color: 'rgba(192, 192, 192, 0.4)',
        fontSize: 11,
        fontStyle: 'italic',
      }}>
        No monsters in encounter. Spawn from the Bestiary above.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px', marginBottom: 4 }}>
        <span style={{ fontSize: 10, color: 'rgba(192, 192, 192, 0.5)' }}>
          {aliveMobs.length} alive{deadMobs.length > 0 ? ` · ${deadMobs.length} dead` : ''}
        </span>
        {allMobs.length > 0 && (
          <div style={{ display: 'flex', gap: 4 }}>
            {!confirmClearAll ? (
              <button
                onClick={() => setConfirmClearAll(true)}
                title="Remove all mobs + any orphan tokens (preserves PCs and dead mobs' bodies)"
                style={{
                  padding: '1px 6px', fontSize: 9,
                  backgroundColor: 'rgba(239, 68, 68, 0.1)', color: 'rgba(239, 68, 68, 0.6)',
                  border: '1px solid rgba(239, 68, 68, 0.15)', borderRadius: 2, cursor: 'pointer',
                }}
              >Clear All</button>
            ) : (
              <>
                <button
                  onClick={handleClearAllMobs}
                  style={{
                    padding: '1px 6px', fontSize: 9, fontWeight: 700,
                    backgroundColor: '#ef4444', color: '#fff',
                    border: '1px solid #ef4444', borderRadius: 2, cursor: 'pointer',
                  }}
                >Confirm</button>
                <button
                  onClick={() => setConfirmClearAll(false)}
                  style={{
                    padding: '1px 6px', fontSize: 9,
                    backgroundColor: 'rgba(192, 192, 192, 0.05)', color: 'rgba(192, 192, 192, 0.5)',
                    border: '1px solid rgba(192, 192, 192, 0.1)', borderRadius: 2, cursor: 'pointer',
                  }}
                >Cancel</button>
              </>
            )}
            <button
              onClick={handleClearEncounter}
              style={{
                padding: '1px 6px', fontSize: 9,
                backgroundColor: 'rgba(239, 68, 68, 0.1)', color: 'rgba(239, 68, 68, 0.6)',
                border: '1px solid rgba(239, 68, 68, 0.15)', borderRadius: 2, cursor: 'pointer',
              }}
            >End Encounter</button>
          </div>
        )}
      </div>

      {/* Alive mobs */}
      {aliveMobs.map((mob) => (
        <div key={mob.id}>
          <MobRow
            mob={mob}
            isExpanded={expandedId === mob.id}
            onToggle={() => setExpandedId(expandedId === mob.id ? null : mob.id)}
          />
          {expandedId === mob.id && (
            <MobDetail mob={mob} onClose={() => setExpandedId(null)} />
          )}
        </div>
      ))}

      {/* Dead section */}
      {deadMobs.length > 0 && (
        <>
          <div
            onClick={() => setShowDead(!showDead)}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '4px 8px', marginTop: 4,
              cursor: 'pointer', fontSize: 10, color: 'rgba(192, 192, 192, 0.4)',
            }}
          >
            <Icon name={showDead ? 'chevron-down' : 'chevron-right'} size={10} color="rgba(192, 192, 192, 0.3)" />
            Dead ({deadMobs.length})
          </div>
          {showDead && deadMobs.map((mob) => (
            <div key={mob.id}>
              <MobRow
                mob={mob}
                isExpanded={expandedId === mob.id}
                onToggle={() => setExpandedId(expandedId === mob.id ? null : mob.id)}
              />
              {expandedId === mob.id && (
                <MobDetail mob={mob} onClose={() => setExpandedId(null)} />
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// =============================================================================
// MobSpriteControls — iso sprite + token portrait management for one mob
// =============================================================================

/** Read a File as a data URL. */
function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Persist the iso sprites for this mob to its owning row (bestiary template
 *  if the mob was spawned from one; else the per-instance mob row). */
async function persistSpritesForMob(mob: Mob, sprites: Record<string, string>): Promise<void> {
  const facings = {
    south: sprites.south,
    north: sprites.north,
    east: sprites.east,
    west: sprites.west,
  };
  if (!facings.south || !facings.north || !facings.east || !facings.west) return;
  try {
    if (mob.bestiaryId) {
      await trpc.sprites.saveToBestiary.mutate({ bestiaryId: mob.bestiaryId, sprites: facings });
    } else {
      await trpc.sprites.saveToMob.mutate({ mobId: mob.id, sprites: facings });
    }
  } catch (err) {
    console.warn('[MobSpriteControls] persist failed:', err);
  }
}

function MobSpriteControls({ mob }: { mob: Mob }) {
  const facingCount = useIsoSpriteStore((s) => s.facingCount(mob.id));
  const currentSprite = useIsoSpriteStore((s) => s.getCurrentSprite(mob.id));
  const isBusy = useIsoSpriteStore((s) => s.isGenerating(mob.id));
  const [customBusy, setCustomBusy] = useState(false);
  const [tokenBusy, setTokenBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const spriteFileRef = useRef<HTMLInputElement>(null);
  const tokenFileRef = useRef<HTMLInputElement>(null);

  const linkedToken = useTokenStore((s) => s.getTokenByCombatantId(mob.id));
  const portraitUrl = linkedToken?.visuals?.imageUrl;

  const handleRegen = async () => {
    if (isBusy) return;
    setError(null);
    try {
      const store = useIsoSpriteStore.getState();
      store.clearForId(mob.id);
      const base = buildMobSpriteParams(mob);
      await store.generateAllFacings({
        ...base,
        persistTarget: mob.bestiaryId
          ? { kind: 'bestiary', bestiaryId: mob.bestiaryId }
          : { kind: 'mob', mobId: mob.id },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Regen failed');
    }
  };

  const handleUploadSprite = async (file: File) => {
    setCustomBusy(true);
    setError(null);
    try {
      const dataUrl = await readFileAsDataURL(file);
      const store = useIsoSpriteStore.getState();
      store.setAllFacings(mob.id, dataUrl, mob.displayName || mob.name);
      // Also persist to bestiary/mob row so it survives refresh + propagates.
      await persistSpritesForMob(mob, {
        south: dataUrl, north: dataUrl, east: dataUrl, west: dataUrl,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setCustomBusy(false);
    }
  };

  const handleSwapToken = async (file: File) => {
    if (!linkedToken) return;
    setTokenBusy(true);
    setError(null);
    try {
      const dataUrl = await readFileAsDataURL(file);
      useTokenStore.getState().updateToken({
        id: linkedToken.id,
        visuals: { ...linkedToken.visuals, imageUrl: dataUrl },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Swap failed');
    } finally {
      setTokenBusy(false);
    }
  };

  // Iso ships a single forward sprite (4-direction set is a dormant future
  // spec — see isoSpriteStore.ACTIVE_FACINGS). Any sprite present = Ready.
  const spriteStatus =
    isBusy ? 'Generating…' :
    facingCount === 0 ? 'No sprite' :
    'Ready';

  const statusColor = isBusy ? '#fbbf24' : facingCount === 0 ? 'rgba(192,192,192,0.4)' : '#4ade80';

  return (
    <div style={{
      marginTop: 4,
      padding: 6,
      backgroundColor: 'rgba(30, 20, 40, 0.4)',
      border: '1px solid rgba(212, 175, 55, 0.15)',
      borderRadius: 4,
    }}>
      <div style={{ fontSize: 9, color: 'rgba(192,192,192,0.5)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Sprite & Images
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        {/* Thumbnails */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 4, overflow: 'hidden',
            backgroundColor: 'rgba(0,0,0,0.4)',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }} title="Iso sprite (south facing)">
            {currentSprite ? (
              <img src={currentSprite} alt="sprite" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <Icon name="users" size={18} color="rgba(192,192,192,0.3)" />
            )}
          </div>
          <div style={{
            width: 48, height: 48, borderRadius: '50%', overflow: 'hidden',
            backgroundColor: 'rgba(0,0,0,0.4)',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }} title="Token portrait (2D view)">
            {portraitUrl ? (
              <img src={portraitUrl} alt="portrait" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Icon name="token" size={18} color="rgba(192,192,192,0.3)" />
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 10, color: statusColor, fontWeight: 600 }}>{spriteStatus}</div>
          {mob.bestiaryId && (
            <div style={{ fontSize: 9, color: 'rgba(192,192,192,0.4)' }}>
              Syncs to bestiary template
            </div>
          )}

          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
            <button
              onClick={handleRegen}
              disabled={isBusy}
              title="Regenerate all 4 iso facings via FLUX"
              style={{
                padding: '3px 7px', fontSize: 10, fontWeight: 600,
                backgroundColor: 'rgba(212, 175, 55, 0.12)', color: GOLD,
                border: '1px solid rgba(212, 175, 55, 0.3)', borderRadius: 3,
                cursor: isBusy ? 'wait' : 'pointer', opacity: isBusy ? 0.5 : 1,
                display: 'flex', alignItems: 'center', gap: 3,
              }}
            >
              <Icon name="refresh-cw" size={10} color={GOLD} />
              {facingCount === 4 ? 'Regen Sprite' : 'Gen Sprite'}
            </button>

            <button
              onClick={() => spriteFileRef.current?.click()}
              disabled={customBusy}
              title="Upload your own image — used for all 4 facings"
              style={{
                padding: '3px 7px', fontSize: 10, fontWeight: 600,
                backgroundColor: 'rgba(136, 104, 198, 0.12)', color: '#b088d4',
                border: '1px solid rgba(136, 104, 198, 0.3)', borderRadius: 3,
                cursor: customBusy ? 'wait' : 'pointer', opacity: customBusy ? 0.5 : 1,
                display: 'flex', alignItems: 'center', gap: 3,
              }}
            >
              <Icon name="upload" size={10} color="#b088d4" />
              Upload Sprite
            </button>

            <button
              onClick={() => tokenFileRef.current?.click()}
              disabled={tokenBusy || !linkedToken}
              title="Replace the 2D token portrait (used in top-down view)"
              style={{
                padding: '3px 7px', fontSize: 10, fontWeight: 600,
                backgroundColor: 'rgba(74, 222, 128, 0.12)', color: '#4ade80',
                border: '1px solid rgba(74, 222, 128, 0.3)', borderRadius: 3,
                cursor: tokenBusy || !linkedToken ? 'not-allowed' : 'pointer',
                opacity: tokenBusy || !linkedToken ? 0.5 : 1,
                display: 'flex', alignItems: 'center', gap: 3,
              }}
            >
              <Icon name="upload" size={10} color="#4ade80" />
              Swap Portrait
            </button>
          </div>

          {error && (
            <div style={{ fontSize: 9, color: '#ef4444', marginTop: 2 }}>{error}</div>
          )}
        </div>
      </div>

      <input
        ref={spriteFileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleUploadSprite(file);
          e.target.value = '';
        }}
      />
      <input
        ref={tokenFileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleSwapToken(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}
